/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";

import PropTypes from "prop-types";
import _filter from "lodash/filter";
import styled from "styled-components";

import blueGrey from "@material-ui/core/colors/blueGrey";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import ClearIcon from "@material-ui/icons/Clear";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Grid, Paper, Checkbox, Chip, InputAdornment } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import StyledTextField from "./StyledTextField";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const StyledChip = styled(Chip)`
  color: #fff !important;
  background-color: ${blueGrey[700]} !important;
  padding: 0px 4px 0px 0px !important;
  .MuiChip-deleteIcon {
    background-color: white;
    color: ${blueGrey[700]};
    height: 15px;
    width: 15px;
    border-radius: 100%;
  }
`;

const AutoCompleteMultiSelect = ({
  label,
  disabled,
  options,
  error,
  setError,
  errorMessage,
  selectedOptions,
  setSelectedOptions,
  chipsContainerStyles,
  autoCompleteTestId,
  chipsDescription,
  maxNumberOfSelectedOptions,
  containChips,
  autoCompleteStyles,
}) => {
  const [inputValue, setInputValue] = useState("");
  const selectedIds = selectedOptions.map((value) => value.id);

  const chips = selectedOptions.map((option) => {
    // this is to handle new options added by the user (allowed by freeSolo prop).
    return (
      <StyledChip
        data-testid={`selected-option-chip-${option.id}`}
        key={option.id}
        label={option.name}
        deleteIcon={<ClearIcon />}
        onDelete={() => {
          setSelectedOptions(
            selectedOptions.filter((entry) => entry.id !== option.id)
          );
        }}
        style={{ margin: "2px" }}
      />
    );
  });

  // since the chips are separate from the auto complete input we need to manage removing an association when manually un-checking a selected option
  const handleNewSelection = (newList, option) => {
    setError(false);
    let selectedOptions = newList;
    if (selectedIds.includes(option.option.id)) {
      selectedOptions = _filter(newList, (value) => {
        return value.id !== option.option.id;
      });
    }

    if (selectedOptions.length <= maxNumberOfSelectedOptions) {
      setSelectedOptions(selectedOptions);
    }
  };

  const handleInputValueChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleFilterOptions = () => {
    return _filter(options, (option) => {
      return (
        option.name &&
        option.name.toLowerCase().includes(inputValue.toLowerCase())
      );
    });
  };

  return (
    <>
      <Autocomplete
        style={autoCompleteStyles}
        data-testid={autoCompleteTestId}
        multiple
        disableCloseOnSelect
        disableClearable
        freeSolo
        renderTags={() => {}}
        disabled={disabled}
        getOptionLabel={(option) => `${option.name}`}
        options={options ? options : []}
        onChange={(e, newList, action, option) => {
          if (e.key !== "Backspace") {
            handleNewSelection(newList, option);
          }
        }}
        filterOptions={handleFilterOptions}
        value={selectedOptions}
        renderOption={(option, { selected }) => (
          <>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selectedIds.includes(option.id)}
              key={option.id}
            />
            {option.name}
          </>
        )}
        renderInput={(params) => {
          return (
            <StyledTextField
              {...params}
              label={label}
              variant="outlined"
              error={error}
              helperText={error && errorMessage}
              onChange={handleInputValueChange}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                value: inputValue,
                "data-testid": "auto-complete-textfield",
              }}
            />
          );
        }}
      />
      {selectedOptions && selectedOptions.length > 0 && (
        <>
          {containChips ? (
            <Grid container>
              <Grid item xs={12}>
                <span style={{ fontSize: "12px" }}>{chipsDescription}</span>
                <Paper style={chipsContainerStyles}>
                  <div>{chips}</div>{" "}
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <>{chips}</>
          )}
        </>
      )}
    </>
  );
};

AutoCompleteMultiSelect.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  error: PropTypes.bool,
  setError: PropTypes.func,
  errorMessage: PropTypes.string,
  selectedOptions: PropTypes.array.isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  chipsContainerStyles: PropTypes.object,
  autoCompleteTestId: PropTypes.string,
  chipsDescription: PropTypes.string,
  maxNumberOfSelectedOptions: PropTypes.number,
};
export default AutoCompleteMultiSelect;
